import { rewriteOneWayCharterInclusions, isConfiguredFeederOriginAirportCode, rewriteCharterFeederInclusions } from 'lib/charterFlights/charterBundleOfferPageUtils'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { Moment } from 'moment'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import { CHARTER_FLIGHT_DETAILS, MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import { hasStructuredPackageInclusionsValidityText } from 'components/OfferPage/Accommodation/PackageInclusionsValidityText'
import { getInclusionsWithoutPackageValidityText } from 'components/OfferPage/Accommodation/packageInclusionValidityUtils'

interface Options {
  offer: App.HotelOffer
  checkIn?: Moment | string
  checkOut?: Moment | string
  rooms?: Array<App.Occupants>
  charterBundleRoute?: App.CharterBundleRoute
  flightOrigin?: App.Airport
  flights?: boolean
  hasFeederFlightPriced?: boolean
  isInclusionsValidityEnabled: boolean
}

export function hasPackageInclusionItems(
  luxPlusInclusions: Array<App.PackageInclusion>,
  inclusions: Array<App.PackageInclusion>,
  bonusInclusions: Array<App.PackageInclusion>,
): boolean {
  return luxPlusInclusions.length > 0 || inclusions.length > 0 || bonusInclusions.length > 0
}

export default function useFlashHotelRateInclusions(pkg: App.Package, options: Options) {
  const {
    offer,
    checkIn,
    checkOut,
    rooms,
    charterBundleRoute,
    flightOrigin,
    flights,
    hasFeederFlightPriced,
    isInclusionsValidityEnabled,
  } = options
  const intl = useIntl()
  const { oneWayBundle } = useOfferPageFlightPrice()
  const { inclusions, bonusInclusions, luxPlusInclusions } = useOfferInclusions(pkg, { checkIn, checkOut })
  const isFeederIncluded = !!offer.campaignTags?.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG) &&
    isConfiguredFeederOriginAirportCode(charterBundleRoute ?? 'melbourne-maldives', flightOrigin?.code) &&
    !!hasFeederFlightPriced
  const effectiveInclusions = useMemo(() => {
    const { originAirport, destinationAirport } = CHARTER_FLIGHT_DETAILS['melbourne-maldives']
    if (oneWayBundle) {
      return rewriteOneWayCharterInclusions(inclusions, {
        feederCity: isConfiguredFeederOriginAirportCode(charterBundleRoute ?? 'melbourne-maldives', flightOrigin?.code) ? flightOrigin?.name : undefined,
        originCity: originAirport.cityDesc,
        destinationCity: destinationAirport.cityDesc,
        intl,
      })
    }
    if (!isFeederIncluded || !flightOrigin) return inclusions

    return rewriteCharterFeederInclusions(inclusions, {
      feederCity: flightOrigin.name,
      originCity: originAirport.cityDesc,
      destinationCity: destinationAirport.cityDesc,
      intl,
    })
  }, [charterBundleRoute, oneWayBundle, flightOrigin, inclusions, intl, isFeederIncluded])
  const shouldShowInclusionsValidityText = isInclusionsValidityEnabled &&
    !!pkg.showInclusionsValidityTag &&
    hasStructuredPackageInclusionsValidityText(pkg.roomPolicyDescription)
  const visibleInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(effectiveInclusions, shouldShowInclusionsValidityText)
  }, [effectiveInclusions, shouldShowInclusionsValidityText])
  const visibleBonusInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(bonusInclusions, shouldShowInclusionsValidityText)
  }, [bonusInclusions, shouldShowInclusionsValidityText])
  return {
    inclusions: visibleInclusions,
    bonusInclusions: visibleBonusInclusions,
    luxPlusInclusions,
    hasInclusions: hasPackageInclusionItems(luxPlusInclusions, visibleInclusions, visibleBonusInclusions),
    shouldShowInclusionsValidityText,
    selectedOccupancy: rooms?.length === 1 ? rooms[0] : undefined,
    // Forced bundles already have an authored flight inclusion; adding a generic one duplicates it.
    showReturnFlightsInclusion: !!flights && !charterBundleRoute && !offer.bundledWithFlightsOnly,
  }
}
